import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Heart, User, Sun, Moon, Menu, X } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { NAV } from "@/constants/testIds";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import SearchDialog from "@/components/SearchDialog";

const links = [
  { to: "/", label: "Home", id: NAV.home },
  { to: "/shop", label: "Shop", id: NAV.shop },
  { to: "/mobile-accessories", label: "Mobile", id: NAV.mobile },
  { to: "/lifestyle", label: "Lifestyle", id: NAV.lifestyle },
  { to: "/about", label: "About", id: NAV.about },
  { to: "/contact", label: "Contact", id: NAV.contact },
];

export default function Navbar() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { count: wl } = useWishlist();
  const nav = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);

  return (
    <>
    <header data-testid={NAV.root} className={`fixed top-0 inset-x-0 z-50 ${theme === "dark" ? "glass-nav-dark" : "glass-nav-light"}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid={NAV.logo} className="group relative flex flex-col items-start leading-none">
          <span
            className="font-heading font-medium tracking-[-0.06em] text-2xl leading-none transition-all duration-500"
            style={{
              background: "linear-gradient(180deg, #FFFFFF 25%, rgba(37,99,235,0.9) 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow: "0 0 22px rgba(37,99,235,0.55)",
            }}
          >
            SSR
          </span>
          <span className="mt-0.5 text-[9px] font-mono uppercase tracking-[0.32em] text-muted-foreground">Innovations</span>
          <span className="absolute -bottom-1 left-0 h-px w-0 bg-electric group-hover:w-full transition-all duration-500" />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.id}
              end={l.to === "/"}
              className={({ isActive }) =>
                `group relative text-sm tracking-wide transition-colors py-1 ${isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span className={`absolute -bottom-0.5 left-1/2 -translate-x-1/2 h-px bg-electric transition-all duration-300 ${isActive ? "w-4" : "w-0 group-hover:w-4"}`} />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1">
          <button data-testid={NAV.search} onClick={() => setSearchOpen(true)} className="w-10 h-10 rounded-full hover:bg-secondary flex items-center justify-center transition-colors" aria-label="Search">
            <Search className="w-4 h-4" />
          </button>
          <button data-testid={NAV.theme} onClick={toggle} className="w-10 h-10 rounded-full hover:bg-secondary flex items-center justify-center transition-colors" aria-label="Toggle theme">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <Link to="/wishlist" data-testid={NAV.wishlist} className="relative w-10 h-10 rounded-full hover:bg-secondary flex items-center justify-center transition-colors" aria-label="Wishlist">
            <Heart className="w-4 h-4" />
            {wl > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-electric text-white text-[10px] font-medium flex items-center justify-center">{wl}</span>}
          </Link>
          <Link to="/cart" data-testid={NAV.cart} className="relative w-10 h-10 rounded-full hover:bg-secondary flex items-center justify-center transition-colors" aria-label="Cart">
            <ShoppingBag className="w-4 h-4" />
            {count > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-electric text-white text-[10px] font-medium flex items-center justify-center">{count}</span>}
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={NAV.account} className="w-10 h-10 rounded-full hover:bg-secondary flex items-center justify-center transition-colors" aria-label="Account">
                <User className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {user ? (
                <>
                  <DropdownMenuLabel className="font-normal">
                    <div className="text-xs text-muted-foreground">Signed in as</div>
                    <div className="font-medium truncate">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => nav("/track-order")} data-testid="menu-track-order">Track Order</DropdownMenuItem>
                  {user.role === "admin" && <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">Admin</DropdownMenuItem>}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="menu-logout">Logout</DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuItem onClick={() => nav("/login")} data-testid="menu-login">Login / Register</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/track-order")} data-testid="menu-guest-track">Track Order</DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <button data-testid={NAV.menu} onClick={() => setOpen((o) => !o)} className="lg:hidden w-10 h-10 rounded-full hover:bg-secondary flex items-center justify-center transition-colors" aria-label="Menu">
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-border/40">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-2">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-2 text-sm ${isActive ? "text-foreground" : "text-muted-foreground"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
    <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
}
